import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { logInfo } from "@/Utils/logger";
import {
  getClientIdParam,
  wasClientIdCaseMangled,
} from "@/Utils/deepLinkParams";
import "./App.scss";
import {
  BrowserRouter as Router,
  useRoutes,
  useLocation,
} from "react-router-dom";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/Utils/queryClient";
import { AnimatePresence } from "motion/react";
import AnimatedPage from "@/Components/AnimatedPage";
import { NavigationProvider } from "@/context/navigation.context";
import { PoseDetectionManager } from "@/services/detection/PoseDetectionManager";
import { ScanUploader } from "@/services/ScanUploader";
import { ImagePreloader } from "@/Utils/ImagePreloader";
import { AudioManager } from "@/services/AudioManager";
import { AssetLoadingService } from "@/services/AssetLoadingService";
import OfflineBanner from "@/Components/OfflineBanner";
import {
  allImagesLoadedAtom,
  allAudiosLoadedAtom,
  imageLoadingRetryAtom,
  audioLoadingRetryAtom,
} from "@/atoms/scanning";
import { useSetAtom } from "jotai";
import { auth, db } from "@/Utils/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  setAvatarSharingData,
  setDeeplinkFlag,
  initializeEnvironmentBasedSharing,
  isStyleMatchClientId,
  isSisleyClientId,
} from "@/Utils/avatarSharingStorage";

// Direct imports
import AvatarPreview from "./Pages/AvatarPreview";
import AvatarSelect from "./Pages/AvatarSelect";
import AvatarGeneration from "./Pages/AvatarGeneration";
import AvatarProcessing from "./Pages/AvatarProcessing";
import AvatarFailed from "./Pages/AvatarFailed";
import SendMeasurements from "./Pages/SendMeasurements";
import ShareCancelled from "./Pages/ShareCancelled";
import ContactUs from "./Pages/ContactUs";
import AvatarScreen from "./Pages/AvatarScreen";
import Settings from "./Pages/Settings";
import NewAvatarScreen from "./Pages/NewAvatarScreen";
import SubmitScan from "./Pages/SubmitScan";
import RedeemLink from "./Pages/RedeemLink";
import SplashScreen from "./Components/SplashScreen";
import { Outcomes, Phases } from "@/telemetry/events";
import { exitPhase } from "@/telemetry/phases";
import {
  createSplashBudget,
  withinBudget,
} from "@/Utils/splashReadBudget";

// Keep only ScanningScreen as lazy loaded
const ScanningScreen = lazy(() => import("./Pages/ScanningScreen/index"));

// Dev-only (spec 2026-09-11 §4.7). `REACT_APP_IS_PROD_ENV` is inlined at build
// time, so a prod build folds this to `null` and never emits the chunk; the page
// also refuses to work without a pose endpoint, which prod builds lack.
const DevBenchmark =
  process.env.REACT_APP_IS_PROD_ENV !== "true"
    ? lazy(() => import("./Pages/DevBenchmark"))
    : null;

const routes = [
  {
    path: "/",
    element: <AvatarScreen />,
  },
  {
    path: "/redeem",
    element: <RedeemLink />,
  },
  {
    path: "/settings",
    element: <Settings />,
  },
  {
    path: "/new-avatar-screen",
    element: <NewAvatarScreen />,
  },
  {
    path: "/submit-scan",
    element: <SubmitScan />,
  },
  {
    path: "/avatar-preview",
    element: <AvatarPreview />,
  },
  {
    path: "/send-avatar",
    element: <AvatarSelect />,
  },
  {
    path: "/measurements-sent",
    element: <SendMeasurements />,
  },
  {
    path: "/share-cancelled",
    element: <ShareCancelled />,
  },
  {
    path: "/avatar-failed",
    element: <AvatarFailed />,
  },
  {
    path: "/avatar-generation",
    element: <AvatarGeneration />,
  },
  {
    path: "/avatar-processing",
    element: <AvatarProcessing />,
  },
  {
    path: "/start-scan",
    element: (
      <Suspense fallback={null}>
        <ScanningScreen />
      </Suspense>
    ),
  },
  {
    path: "/contact-us",
    element: <ContactUs />,
  },
  ...(DevBenchmark
    ? [
        {
          path: "/dev/benchmark",
          element: (
            <Suspense fallback={null}>
              <DevBenchmark />
            </Suspense>
          ),
        },
      ]
    : []),
];

const AppContent: React.FC = () => {
  const element = useRoutes(routes);
  const location = useLocation();
  const poseDetectionManager = useMemo(
    () => PoseDetectionManager.getInstance(),
    [],
  );
  // initialize scan uploader to provide the upload data across the app
  useMemo(() => ScanUploader.getInstance(), []);
  // Keep references to ensure singletons are initialized
  useMemo(() => ImagePreloader.getInstance(), []);
  useMemo(() => AudioManager.getInstance(), []);
  const assetLoadingService = useMemo(
    () => AssetLoadingService.getInstance(),
    [],
  );

  const setAllImagesLoaded = useSetAtom(allImagesLoadedAtom);
  const setAllAudiosLoaded = useSetAtom(allAudiosLoadedAtom);
  const setImageLoadingRetry = useSetAtom(imageLoadingRetryAtom);
  const setAudioLoadingRetry = useSetAtom(audioLoadingRetryAtom);

  useEffect(() => {
    poseDetectionManager?.initialize("MoveNet");

    // Initialize environment-based sharing for Malchus/Neems
    initializeEnvironmentBasedSharing();

    // Set up asset loading service with state management
    assetLoadingService.onStateChange((state) => {
      setAllImagesLoaded(state.imagesLoaded);
      setAllAudiosLoaded(state.audiosLoaded);

      setImageLoadingRetry({
        isRetrying: state.isRetrying && state.imageRetryCount > 0,
        retryCount: state.imageRetryCount,
        failedImages: state.failedImages,
      });

      setAudioLoadingRetry({
        isRetrying: state.isRetrying && state.audioRetryCount > 0,
        retryCount: state.audioRetryCount,
        failedAudios: state.failedAudios,
      });
    });

    // Initialize assets with comprehensive retry mechanism
    assetLoadingService.initializeAssets().catch((error) => {
      console.error("Failed to initialize assets:", error);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loading, setLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  // Set when auth produced no user within the splash timeout — switches the
  // splash to a "can't connect" + Retry state. The app is unusable without a
  // Firebase user (every query/write is keyed on auth.uid and enforced by
  // Firestore rules; anonymous auth is the guaranteed floor), so when even
  // the anon safety net failed there is nothing to route to — an honest
  // retry beats an app shell that pretends to load forever.
  const [authTimedOut, setAuthTimedOut] = useState(false);
  // Mirrors of `loading`/`showSpinner` for the splash-timeout callback below —
  // the effect closure would otherwise capture the initial values forever.
  // Close the `boot` telemetry phase when the splash actually clears.
  //
  // Keyed on `loading` — the value that decides what RENDERS — rather than
  // patched into each of the eight `setLoading(false)` call sites. That is
  // PLAN.md §5.1: log where the state rendered, not where the branch was
  // decided. A session that never clears the splash leaves `boot` open, and the
  // pagehide hook closes it as `abandoned` — which is exactly how "stuck on
  // splash" becomes a rate instead of an anecdote.
  useEffect(() => {
    if (!loading) exitPhase(Phases.BOOT, Outcomes.ADVANCED);
  }, [loading]);

  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  const showSpinnerRef = useRef(showSpinner);
  useEffect(() => {
    showSpinnerRef.current = showSpinner;
  }, [showSpinner]);
  const { navigateTo } = useCustomNavigation();
  const isMalchusEnv = process.env.REACT_APP_IS_MALCHUS_ENV === "true";
  const isNeemsEnv = process.env.REACT_APP_IS_NEEMS_ENV === "true";
  const isStyleMatchEnv = process.env.REACT_APP_IS_STYLEMATCH_ENV === "true";
  const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

  useEffect(() => {
    let isMounted = true;

    const handleSendAvatarRoute = async (user: any) => {
      setShowSpinner(true);
      // Check if user is trying to access /send-avatar route
      const urlParams = new URLSearchParams(window.location.search);
      const currentPath = window.location.pathname;

      if (currentPath !== "/send-avatar") {
        // Not send-avatar route, proceed normally
        if (isMounted) setLoading(false);
        return;
      }

      // User is accessing /send-avatar route
      // Read case-insensitively: a share link retyped or pasted WITHOUT its
      // scheme has its query lowercased by the browser omnibox, so `clientId`
      // arrives as `clientid`. sessionId/redirectUrl below stay strict on
      // purpose — their values are case-sensitive, so key matching cannot save
      // them. See @/Utils/deepLinkParams.
      const clientId = getClientIdParam(window.location.search);
      if (wasClientIdCaseMangled(window.location.search)) {
        // Rare by construction (only mangled links reach here), so this is not
        // a chatty write — and it is the only signal that stale schemeless
        // links are still in circulation.
        logInfo("[App] send-avatar clientId recovered from lowercased key", {
          clientId,
        });
      }
      const sessionId = urlParams.get("sessionId");
      let redirectUrl = urlParams.get("redirectUrl");

      // Validate environment-specific client IDs
      if (isMalchusEnv && clientId && clientId !== "715053") {
        if (isMounted) setLoading(false);
        return;
      }
      if (isNeemsEnv && clientId && clientId !== "553777") {
        if (isMounted) setLoading(false);
        return;
      }
      // Sisley runs the parent clientId (417017) AND its linked sub-accounts on
      // this one branded build — accept any of them, reject anything else.
      if (isSisleyEnv && clientId && !isSisleyClientId(clientId)) {
        if (isMounted) setLoading(false);
        return;
      }
      // StyleMatch runs two clientIds (prod 809856 + staging 542101) on this one
      // branded build — accept either, reject anything else.
      if (isStyleMatchEnv && clientId && !isStyleMatchClientId(clientId)) {
        if (isMounted) setLoading(false);
        return;
      }

      // ONE deadline for every Firestore read below, not one per read. The
      // splash is gated on this whole function, so a per-read timeout would
      // still let four sequential reads hold it for 4x the budget.
      const budget = createSplashBudget();

      try {
        // Check if user has any scans
        const scansQuery = query(
          collection(db, "scans"),
          where("userId", "==", user.uid),
        );
        const scansSnapshot = await withinBudget(
          getDocs(scansQuery),
          budget,
          null,
          "scans",
        );
        // On timeout, fall through as "no scans" — which sends the user to
        // /start-scan. That is not a guess: the sessions that time out are
        // overwhelmingly cache-cold first-time visitors, who genuinely have
        // none, so the fallback matches the answer the read would have given.
        const hasScans = scansSnapshot !== null && scansSnapshot.size > 0;

        // If clientId is provided, fetch organization info and store it
        if (clientId) {
          let orgName = "";
          try {
            const clientIdQuery = query(
              collection(db, "clientIds"),
              where("clientId", "==", clientId),
            );
            const querySnapshot = await withinBudget(
              getDocs(clientIdQuery),
              budget,
              null,
              "clientIds",
            );
            if (querySnapshot !== null && !querySnapshot.empty) {
              const documentData = querySnapshot.docs[0].data();
              orgName =
                documentData.orgName?.trim() || documentData.name?.trim() || "";
            }
          } catch (error) {
            console.error("Error fetching organization name:", error);
          }

          // Decode the redirect URL if it's encoded
          if (redirectUrl) {
            try {
              console.log("Redirect URL before decoding:", redirectUrl);

              // Decode the URL (may need multiple passes if double/triple encoded)
              let decodedUrl = redirectUrl;
              let previousUrl = "";

              // Keep decoding until the URL doesn't change (handles multiple encoding levels)
              while (decodedUrl !== previousUrl) {
                previousUrl = decodedUrl;
                try {
                  decodedUrl = decodeURIComponent(decodedUrl);
                } catch (e) {
                  // Stop if decoding fails
                  break;
                }
              }

              redirectUrl = decodedUrl;
              console.log("Redirect URL after decoding:", redirectUrl);
            } catch (error) {
              console.error("Error decoding redirect URL:", error);
            }
          }

          // Store sharing data
          setAvatarSharingData({
            clientId,
            sessionId: sessionId || null,
            orgName: orgName || null,
            shareCode: null,
            redirectUrl: redirectUrl || null,
          });

          setDeeplinkFlag();

          // Update widget session if sessionId exists
          if (sessionId) {
            try {
              const sessionRef = doc(db, "widget_sessions", sessionId);
              const sessionDoc = await withinBudget(
                getDoc(sessionRef),
                budget,
                null,
                "widgetSession",
              );
              if (sessionDoc !== null && sessionDoc.exists()) {
                const { activeScan } = sessionDoc.data();
                // If session already has an active scan, user already shared
                if (activeScan) {
                  if (isMounted) {
                    navigateTo("/measurements-sent", {
                      state: { orgName },
                      replace: true,
                    });
                    setLoading(false);
                  }
                  return;
                }
                // Mark session as started
                await withinBudget(
                  updateDoc(sessionRef, {
                    progress: "STARTED",
                    activeScan: null,
                  }),
                  budget,
                  undefined,
                  "widgetSessionUpdate",
                );
              }
            } catch (error) {
              console.error("Error updating widget session:", error);
            }
          }
        }

        // Decide where to navigate based on scan existence
        if (!hasScans) {
          // No scans - redirect to start-scan
          // Note: Component will check storage utility to determine if it's a deepLink
          if (isMounted) {
            console.log("Navigating to start-scan");
            navigateTo("/start-scan", {
              replace: true,
            });
            setLoading(false);
          }
        } else {
          // Has scans - allow normal navigation to AvatarSelect
          if (isMounted) setLoading(false);
        }
      } catch (error) {
        console.error("Error checking scans:", error);
        // On error, fallback to showing the page
        if (isMounted) setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await handleSendAvatarRoute(user);
      } else if (window.location.pathname.startsWith("/redeem") && isMounted) {
        // The /redeem magic-link route owns its own sign-in (RedeemLink signs
        // in the scan OWNER). The auth-context guards deliberately skip the anon
        // sign-in here, so onAuthStateChanged never fires with a user on /redeem
        // — and this gate clears `loading` only when a user arrives. Without
        // this branch the route deadlocks on the splash forever (the component
        // that would create the user never renders). Clear loading immediately
        // so RedeemLink can mount and do the owner exchange. No sign-in happens
        // here — see src/Pages/RedeemLink and HERE_BE_DRAGONS §magic-link.
        setLoading(false);
      }
    });

    // Defensive net for the splash gate. `loading` is cleared only when
    // onAuthStateChanged delivers a user (via handleSendAvatarRoute) or by the
    // /redeem branch above — there is no null-user fallback, so a session
    // where auth NEVER yields a user (signInAnonymously rejected with
    // auth/network-request-failed or auth/requests-from-referer-*-blocked,
    // the /anon-user fetch hung with no timeout, Firebase never fired) spins
    // on the splash forever. 8s is comfortably past auth.context's 3s
    // endpoint-timeout plus a sign-in round trip, so on healthy sessions a
    // user arrives (and clears loading) long before this fires; it only
    // triggers when every auth layer has already failed or hung.
    //
    // We deliberately do NOT clear `loading` here: with no user the routes
    // are dead ends (AvatarScreen's own loader waits on `user` forever), so
    // instead the splash switches to a "can't connect" + Retry state. Retry
    // reloads the page, restarting the full auth flow — the same
    // last-resort-reload pattern AudioManager/ImagePreloader use. If auth
    // succeeds late, the normal user-fire path clears `loading` and the
    // retry screen self-dismisses.
    // The `showSpinnerRef` gate: `showSpinner` flips true the moment a user
    // ARRIVES (first line of handleSendAvatarRoute). On a slow /send-avatar
    // deep link the Firestore reads there can outlast the 8s while the app is
    // actually working — auth already succeeded, so the "no user" retry state
    // (and its log line) must not fire. That hung-mid-route case keeps the
    // spinner, which is the pre-fix behavior for it.
    const splashTimeout = setTimeout(() => {
      if (isMounted && loadingRef.current && !showSpinnerRef.current) {
        logInfo("[App] splash timeout — no user after 8s, showing retry", {
          path: window.location.pathname,
        });
        setAuthTimedOut(true);
      }
    }, 8000);

    return () => {
      isMounted = false;
      clearTimeout(splashTimeout);
      unsubscribe();
    };
    // navigateTo is stable and doesn't need to be in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMalchusEnv, isNeemsEnv, isStyleMatchEnv]);

  console.log({ loading, showSpinner });

  if (loading) {
    return (
      <div className="App">
        <main>
          <SplashScreen showSpinner={showSpinner} showRetry={authTimedOut} />
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <main>
        <QueryClientProvider client={queryClient}>
          <AnimatePresence mode="sync" initial={true}>
            <div key={location.key}>
              <AnimatedPage>{element}</AnimatedPage>
            </div>
          </AnimatePresence>
        </QueryClientProvider>
      </main>
    </div>
  );
};

const AppWrapper: React.FC = () => (
  <Router>
    <NavigationProvider>
      <OfflineBanner />
      <AppContent />
    </NavigationProvider>
  </Router>
);

export default AppWrapper;
